import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../cn";

type TooltipSide = "top" | "bottom" | "left" | "right";

interface TooltipProps extends Omit<ComponentPropsWithoutRef<"div">, "label"> {
  /** Content rendered inside the floating bubble. `null`/`undefined` renders no bubble at all — the trigger becomes a plain, inert wrapper. */
  label: ReactNode;
  /** The trigger; hovering or focusing within it reveals the bubble. */
  children: ReactNode;
  /** Which edge of the trigger the bubble prefers to anchor to. Falls back to another side via `flip()` when there isn't room. Defaults to `top`. */
  side?: TooltipSide;
  /** Extra classes for the trigger wrapper, e.g. layout constraints. */
  className?: string;
}

/**
 * Hover/focus tooltip, portaled to `document.body` via floating-ui so the
 * bubble can escape any `overflow-hidden` ancestor — a collapsible panel, a
 * scrollable card, a dialog — instead of being clipped by it. Positioning
 * (including flipping to the opposite side and shifting along the axis when
 * the preferred placement would overflow the viewport) is handled by
 * floating-ui's `offset`/`flip`/`shift` middleware and kept in sync with
 * scrolling/resizing via `autoUpdate`.
 *
 * The bubble is purely decorative (`aria-hidden`): its typical job is to
 * reveal text that is already in the accessibility tree but visually
 * truncated, so exposing it again would only cause duplicate screen-reader
 * announcements. A caller that needs the explanation itself to reach
 * assistive tech (e.g. why a control is disabled) wires its own element
 * through `aria-describedby` — that, not this bubble, carries the reason.
 *
 * Reveals on pointer hover and on keyboard focus landing anywhere inside the
 * trigger (via the bubbling `focusin`/`focusout` events — the native
 * equivalent of CSS `:focus-within`). The listeners are attached
 * imperatively to the reference node rather than as JSX `onPointerEnter`/
 * `onFocus` props: this wrapper carries no semantics of its own (the
 * interactive content, if any, is whatever the caller passed as `children`),
 * so there is no ARIA role that would make attaching them as JSX handlers
 * correct rather than merely quiet.
 *
 * For non-interactive triggers (e.g. plain text), pass `tabIndex={0}` so the
 * wrapper can receive keyboard focus and the tooltip becomes reachable.
 *
 * Pass `label={null}` (or `undefined`) to disable the tooltip entirely, e.g.
 * for a trigger whose content isn't currently truncated/clipped and so has
 * nothing to reveal. The wrapper still renders — only the bubble is skipped
 * — so toggling `label` never remounts the trigger's own subtree.
 */
export function Tooltip({
  label,
  children,
  side = "top",
  className,
  ...rest
}: TooltipProps): ReactNode {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles } = useFloating({
    placement: side,
    open,
    // Portaled directly to `document.body`; `fixed` positions relative to
    // the viewport regardless of any positioned ancestor between body and
    // the portal target, so the bubble never depends on incidental layout
    // elsewhere on the page.
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  useEffect(() => {
    const node = refs.reference.current;
    if (!(node instanceof HTMLElement)) return;

    const show = () => setOpen(true);
    const hide = () => setOpen(false);

    node.addEventListener("pointerenter", show);
    node.addEventListener("pointerleave", hide);
    node.addEventListener("focusin", show);
    node.addEventListener("focusout", hide);

    return () => {
      node.removeEventListener("pointerenter", show);
      node.removeEventListener("pointerleave", hide);
      node.removeEventListener("focusin", show);
      node.removeEventListener("focusout", hide);
    };
  }, [refs.reference]);

  return (
    <div ref={refs.setReference} className={className} {...rest}>
      {children}
      {label != null &&
        createPortal(
          <span
            ref={refs.setFloating}
            style={floatingStyles}
            aria-hidden="true"
            className={cn(
              "pointer-events-none z-50 w-max max-w-xs rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-foreground shadow-lg transition duration-150 ease-out",
              open ? "scale-100 opacity-100" : "scale-95 opacity-0",
            )}
          >
            {label}
          </span>,
          document.body,
        )}
    </div>
  );
}
